import { useCallback, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Opens a stored file (SDS, SOP, report, equipment doc) inside the app.
// Never use window.open on a fetched blob — popup blockers swallow it silently.
export function useFileViewer() {
  const [viewer, setViewer] = useState(null);

  const close = useCallback(() => {
    setViewer((v) => {
      if (v?.url) URL.revokeObjectURL(v.url);
      return null;
    });
  }, []);

  const open = useCallback(async (path, { filename = "document", title = "", forceSave = false } = {}) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const type = res.data.type || "";
      const url = URL.createObjectURL(res.data);
      const inline = /pdf|text|image/i.test(type) || /\.(pdf|txt|png|jpe?g)$/i.test(filename);
      if (inline && !forceSave) {
        setViewer({ url, filename, title: title || filename, type });
        return;
      }
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error(apiError(e)); }
  }, []);

  const element = (
    <Dialog open={!!viewer} onOpenChange={(v) => !v && close()}>
      <DialogContent className="h-[92vh] max-w-6xl p-0" data-testid="file-viewer-dialog">
        <DialogHeader className="border-b border-slate-200 px-4 py-3">
          <DialogTitle className="text-base">{viewer?.title}</DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-3">
            <span>{viewer?.filename}</span>
            <a href={viewer?.url} download={viewer?.filename} data-testid="file-viewer-save"
               className="font-semibold text-[#134e4a] hover:underline">Save a copy</a>
            <a href={viewer?.url} target="_blank" rel="noreferrer" data-testid="file-viewer-newtab"
               className="font-semibold text-[#134e4a] hover:underline">Open in a new tab</a>
            <span className="text-xs text-slate-500">Nothing showing? Use "Open in a new tab".</span>
          </DialogDescription>
        </DialogHeader>
        {viewer && (
          <iframe title={viewer.title} src={viewer.url} data-testid="file-viewer-frame"
                  className="h-full w-full border-0 bg-slate-100" />
        )}
      </DialogContent>
    </Dialog>
  );

  return { openFile: open, viewerElement: element };
}
